import { create } from 'zustand';
import { useQuery } from '@tanstack/react-query';
import type { AuthChangeEvent, Session } from '@supabase/supabase-js';
import { sessionNeedsMfa, type MfaStatus } from '@/lib/mfaGate';
import { mfaRepository } from '@/api/authRepository';

// Port of authStateProvider / currentUserProvider from
// lib/core/supabase_service.dart, plus the two-factor state of
// lib/providers/mfa_providers.dart. A small store the App subscribes
// supabase.auth.onAuthStateChange into, so any component can read the live
// session + the last auth event.

interface AuthState {
  session: Session | null;
  event: AuthChangeEvent | null;
  /** True until the first onAuthStateChange fires (initial session resolved). */
  initialized: boolean;
  setAuth: (event: AuthChangeEvent | null, session: Session | null) => void;
  /** The session belongs to an enrolled user who has not entered the code
   *  (mfaPendingProvider, contract §3.1). Recomputed on EVERY auth event;
   *  nothing may read data while it is true. */
  mfaPending: boolean;
  /** A password-reset link signed the user in: they must set a new password
   *  before anything else. Kept apart from `event` (later events such as
   *  INITIAL_SESSION / TOKEN_REFRESHED / MFA_CHALLENGE_VERIFIED overwrite
   *  that) and in sessionStorage per tab so a reload mid-reset — or the
   *  two-factor challenge in between — still lands on the reset screen. */
  recovery: boolean;
  setRecovery: (on: boolean) => void;
  /** An admin call answered 42501 `admin_mfa_required` (§4b): the admin
   *  console shows its gate even if mfa_status said otherwise
   *  (adminMfaForcedProvider). */
  adminMfaForced: boolean;
  setAdminMfaForced: (on: boolean) => void;
}

const RECOVERY_KEY = 'auth.recovery';
function readRecovery(): boolean {
  try { return sessionStorage.getItem(RECOVERY_KEY) === '1'; } catch { return false; }
}

export const useAuthStore = create<AuthState>((set) => ({
  session: null,
  event: null,
  initialized: false,
  mfaPending: false,
  setAuth: (event, session) => set({ event, session, initialized: true, mfaPending: sessionNeedsMfa(session) }),
  recovery: readRecovery(),
  setRecovery: (on) => {
    try {
      if (on) sessionStorage.setItem(RECOVERY_KEY, '1'); else sessionStorage.removeItem(RECOVERY_KEY);
    } catch { /* private mode: the in-memory flag still works for this load */ }
    set({ recovery: on });
  },
  adminMfaForced: false,
  setAdminMfaForced: (on) => set({ adminMfaForced: on }),
}));

export const mfaStatusKey = (uid: string | undefined) => ['mfaStatus', uid ?? ''] as const;

/** The capability probe + the caller's state (mfaStatusProvider, contract
 *  §1, §4b). `data` null = the server has no two-factor (row hidden). An
 *  error is treated as off for this screen and asked again on the next open
 *  (no retries, not cached as an answer). Never runs while the code is
 *  needed. */
export function useMfaStatus() {
  const uid = useAuthStore((s) => s.session?.user.id);
  const pending = useAuthStore((s) => s.mfaPending);
  return useQuery<MfaStatus | null>({
    queryKey: mfaStatusKey(uid),
    queryFn: () => mfaRepository.status(),
    enabled: !!uid && !pending,
    staleTime: Infinity,
    retry: false,
  });
}

/** The admin console shows the two-factor gate instead of the tools
 *  (adminMfaGateProvider): forced by an admin_mfa_required error, or
 *  mfa_status.admin_mfa_required (migration 182; absent before → false). */
export function useAdminMfaGate(): { loading: boolean; gate: boolean; hasFactor: boolean } {
  const forced = useAuthStore((s) => s.adminMfaForced);
  const status = useMfaStatus();
  const hasFactor = status.data?.hasVerifiedFactor ?? false;
  if (forced) return { loading: false, gate: true, hasFactor };
  // mfa_status unreachable: show the tools; the server still refuses them
  // below aal2 and that error opens the gate.
  if (status.isPending && status.fetchStatus !== 'idle') return { loading: true, gate: false, hasFactor };
  return { loading: false, gate: status.data?.adminMfaRequired ?? false, hasFactor };
}
